/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */
import * as StateManager from '../store/state/state-manager';
import * as RuleStore from '../rules/rules-store';
import Utils from '../utils/utils';
import AnnotationProcessor from '../annotation/processor';
import DeferLoadManager from '../deferload/manager';
import constants from '../constants';
import type { PropertyInfo } from '../globals';
import MessageManager from '../messages';

import type { CascadeSubscribersObj, CascadeResetSubscribersObj, CascadeSubscription } from './types';

// This class provide api which can be called explicitly for use like resgistring fields for subscription callback
// and registering resetting dependency.
/**
 * @description
 * Exposes APIs to handle Datapage parameters for callback subscriptions.
 */

class CascadeManager {
  public readonly name: string;
  public cascadeSubscribers: CascadeSubscribersObj;
  public cascadeResetSubscribers: CascadeResetSubscribersObj;
  public listChangeSubscribers: Function[];

  constructor() {
    this.name = 'CascadeManager';
    /*
    {
      "<contextName>":{
        "<propertyReference>": {
          "uniqueId" : "callbackFn"
        }
      }
    }

    callbackFns --> callback(propertyReference, propertyValue)
    */
    /*
   {
     "app/primary":{
       "caseInfo.content.FirstName" : {
         "subscriptions": {
         "firstNameChange" : ()=>{ updateName();}
         },
         "previousValue": "test"
       }
     }
   }
   */
    this.cascadeSubscribers = {};
    /*
    {
      'app/primary_1': {
        'caseInfo.content.productId': ['caseInfo.content.backlogId','caseInfo.content.release']
      }
    }
    */
    this.cascadeResetSubscribers = {};
    this.listChangeSubscribers = [];
  }

  /**
   * Registers all the dependencies for the target field when the source field changes.
   * @description Registers all the dependencies of the target field to perform cascade resetting.
   * Cascade resetting is the process of resetting the value of the target field when the value of the source field changes.
   *
   * @example <caption>In this example, the API registers the dependencies of the backlogId target field.</caption>
   * Example usage - CascadeManager.registerResetDependencies('app/primary_1', 'caseInfo.content','.backlogId', ['.productId','release'], 'Text', 'singleRecord')
   *
   * @param contextName The name of the context containing the fields to be registered.
   * @param pageReference The reference to the page that contains the field to be registered.
   * @param target The field whose dependencies needs to be registered.
   * @param dependentProperties Array of fields on which target field is dependent.
   * @param fieldType The type of the target field which needs to be registered. eg. Text
   * @param mode The selection mode of the target field, the value of mode can singleRecord or multiRecord.
   * @public
   * @function
   */
  registerResetDependencies(
    contextName: string,
    pageReference: string,
    target: string,
    dependentProperties: string[],
    fieldType: string,
    mode = 'singleRecord'
  ) {
    if (!contextName || !target || !dependentProperties?.length) {
      // TODO : We should be Introducing a logger to capture all the Console logs, since logging to console is not a recommended practice.
      return;
    }
    const propertyName = target.startsWith('.') ? `${pageReference}.${target.substring(1)}` : target;
    const currentContextObject: { [key: string]: any } = this.cascadeResetSubscribers[contextName] || {};
    const updatedPageReference = this._handlePageReference(pageReference, target, fieldType);
    dependentProperties.forEach((parameter) => {
      if (!parameter) {
        return;
      }
      let fullPropertyReference = parameter.startsWith('.') ? parameter.substring(1) : parameter;
      fullPropertyReference = Utils.getFullPropertyReference([updatedPageReference, fullPropertyReference]);
      if (fullPropertyReference in currentContextObject) {
        const existingDependentPropertiesSet = currentContextObject[fullPropertyReference];
        const isPropertyFound = existingDependentPropertiesSet.find(
          (prop: { propertyName: string }) => prop.propertyName === propertyName
        );
        if (!isPropertyFound) {
          existingDependentPropertiesSet.push({ propertyName, mode });
        }
      } else {
        currentContextObject[fullPropertyReference] = [{ propertyName, mode }];
      }
    });
    if (Object.keys(currentContextObject).length > 0) {
      this.cascadeResetSubscribers[contextName] = currentContextObject;
    }
  }

  /**
   * De-Registers all the dependencies for the target field when the source field changes.
   * @description De-registers all the registered dependencies of the target field that have undergone cascade resetting.
   * Cascade resetting is the process of resetting the value of the target field when the value of the source field changes.
   *
   * @example <caption>In this example, the API de-registers the registered dependencies of the backlogId target field..</caption>
   * Example usage - CascadeManager.deregisterResetDependencies('app/primary_1', 'caseInfo.content','.backlogId', ['.productId','release'], 'Text')
   *
   * @param contextName The name of the context containing the fields to be de-registered.
   * @param pageReference The reference to the page that contains the field to be de-registered.
   * @param target The field whose dependencies needs to be de-registered.
   * @param dependentProperties Array of fields on which target field is dependent.
   * @param fieldType The type of the target field which needs to be de-registered. eg. Text
   * @public
   * @function
   */

  deregisterResetDependencies(
    contextName: string,
    pageReference: string,
    target: string,
    dependentProperties: string[],
    fieldType: string
  ) {
    if (!contextName || !target || !dependentProperties?.length) {
      // TODO : We should be Introducing a logger to capture all the Console logs, since logging to console is not a recommended practice.
      return;
    }
    /**
     * {
     *  "productID":["productVersionID"]
     *  "EpicID":[]
     *  "CategoryID":["productVersionID"]
     * }
     */
    const propertyName = target.startsWith('.') ? `${pageReference}.${target.substring(1)}` : target;
    const currentContextObject = this.cascadeResetSubscribers[contextName] || {};
    const updatedPageReference = this._handlePageReference(pageReference, target, fieldType);
    dependentProperties.forEach((parameter: string) => {
      if (parameter) {
        let fullPropertyReference = parameter.startsWith('.') ? parameter.substring(1) : parameter;
        fullPropertyReference = Utils.getFullPropertyReference([updatedPageReference, fullPropertyReference]);
        if (fullPropertyReference in currentContextObject) {
          const existingDependentPropertiesSet = currentContextObject[fullPropertyReference];
          Utils.removeExistingDependentProperty(existingDependentPropertiesSet, propertyName);
        }
      }
    });
    if (Object.keys(currentContextObject).length > 0) {
      this.cascadeResetSubscribers[contextName] = currentContextObject;
    }
  }

  /**
   * get all the dependencies associated with the property
   * @param contextName name of the context
   * @param propertyName name of dependent property inside the context
   * @returns dependent properties array which needs to be resetted.
   * @example <caption>Example for getResetDependencies() depicting how to get resetting dependencies for change in productId.</caption>
   * Example usage - CascadeManager.getResetDependencies('app/primary_1', 'caseInfo.content.productId')
   * @private
   */
  getResetDependencies(contextName: string, propertyName: string): PropertyInfo[] {
    return this.cascadeResetSubscribers[contextName]?.[propertyName] || [];
  }

  /**
   * purge dependency map on given contextName
   * @param contextName name of the context
   * @example <caption>Example for purgeResetDependencies() depicting how to purge dependencies on a context.</caption>
   * Example usage - CascadeManager.purgeResetDependencies('app/primary_1')
   * @private
   */
  purgeResetDependencies(contextName: string) {
    if (contextName && this.cascadeResetSubscribers[contextName]) delete this.cascadeResetSubscribers[contextName];
  }

  /**
   * Registers the fields to the CascadeManager Class.
   * @description Register the fields to the CascadeManager Class.
   *
   * @example <caption>Example for registerFields() depicting how to register the firstName and lastName
   * fields to trigger callback.</caption>
   * Example usage - CascadeManager.registerFields('app/primary_1', 'caseInfo.content', ['firstName','lastName'], () => { console.log("field changed")}, '001-002-003')
   *
   * @param context The name of the context containing the fields to be registered.
   * @param pageReference The reference to the page that contains the field to be registered.
   * @param fields Array of fields to be registered.
   * @param callback function to be called when the registered field is updated.
   * @param subscriptionId uniqueId for registering the fields. The same subscriptionId should be provided for de-registering th fields.
   *
   * @function
   * @public
   */
  registerFields(
    context: string,
    pageReference: string,
    fields: string[] | undefined,
    callback: Function,
    subscriptionId: string
  ) {
    this.cascadeSubscribers[context] = this.cascadeSubscribers[context] || {};
    fields?.forEach((field) => {
      this.cascadeSubscribers[context][`${pageReference}${field}`] =
        this.cascadeSubscribers[context][`${pageReference}${field}`] || {};

      const fullPathRef: CascadeSubscription = this.cascadeSubscribers[context][`${pageReference}${field}`];
      if (!fullPathRef[subscriptionId]) {
        fullPathRef[subscriptionId] = callback;
        const val = StateManager.getValue(field, pageReference, context);
        fullPathRef.previousValue = typeof val === 'object' ? null : val;
      }
    });
  }

  /**
   * Registers the fields of type PageList to the CascadeManager Class.
   * @description Registers the field of type PageList to the CascadeManager Class.
   *
   * @example <caption>Example for registerListField() depicting how to register the phoneNumber
   * field to trigger callback. </caption>
   * Example usage - CascadeManager.registerListField('app/primary_1', 'caseInfo.content', phoneNumber, () => { console.log("field changed")}, '002-002-004')
   *
   * @param context The name of the context containing the field to be registered.
   * @param pageReference The reference to the page that contains the field to be registered.
   * @param listField field to be registered.
   * @param callback function to be called when the registered field is updated.
   * @param subscriptionId uniqueId for registering the field. The same subscriptionId should be provided for de-registering th field.
   *
   * @function
   * @public
   */
  registerListField(
    context: string,
    pageReference: string,
    listField: string,
    callback: Function,
    subscriptionId: string
  ) {
    const fieldArray: string[] | undefined = CascadeManager._getFieldsForList(context, pageReference, listField);
    if (fieldArray.length > 0) {
      this.registerFields(context, pageReference, fieldArray, callback, subscriptionId);
      DeferLoadManager.enableCascade(context);
    }
  }

  /**
   * De-Registers the field of type PageList from the CascadeManager Class.
   * @description De-Registers the field of type PageList from the CascadeManager Class.
   *
   * @example <caption>Example for unRegisterListField() depicting how to de-register
   * field.</caption>
   * Example usage - CascadeManager.unRegisterListField('app/primary_1', 'caseInfo.content', phoneNumber, '002-002-004')
   *
   * @param context The name of the context containing the field to be de-registered.
   * @param pageReference The reference to the page that contains the field to be de-registered.
   * @param listField field to be de-registered.
   * @param subscriptionId uniqueId for de-registering the field.
   *
   * @function
   * @public
   */
  unRegisterListField(context: string, pageReference: string, listField: string, subscriptionId: string) {
    const fieldArray: string[] = CascadeManager._getFieldsForList(context, pageReference, listField);
    if (fieldArray.length > 0) {
      this.unRegisterFields(context, pageReference, fieldArray, subscriptionId);
    }
  }

  static _getFieldsForList(context: string, pageReference: string, listField: string) {
    const fieldArray: string[] = [];
    const fieldMetadata = RuleStore.getPropertyMetadata(
      listField,
      StateManager.getValue('classID', pageReference, context)
    );
    if (
      fieldMetadata &&
      !Array.isArray(fieldMetadata) &&
      fieldMetadata.datasource &&
      fieldMetadata.datasource.parameters
    ) {
      Object.values(fieldMetadata.datasource.parameters as unknown as { [key: string]: string }).forEach(
        (param: string) => {
          if (AnnotationProcessor.isProperty(param)) {
            const trimParam = AnnotationProcessor.trimAnnotation(param);
            fieldArray.push(trimParam);
          }
        }
      );
    }
    return fieldArray;
  }

  /**
   * De-registers the fields from the CascadeManager Class.
   * @description De-register fields from the CascadeManager Class.
   *
   * @example <caption>Example for unRegisterFields() depicting how to unregister the firstName and lastName fields.</caption>
   * Example usage - CascadeManager.unRegisterFields('app/primary_1', 'caseInfo.content', ['firstName','lastName'], '001-002-003')
   *
   * @param context The name of the context containing the fields to be de-registered.
   * @param pageReference The reference to the page that contains the field to be de-registered.
   * @param fields Array of fields to be de-registered.
   * @param subscriptionId uniqueId for de-registering the fields.
   *
   * @function
   * @public
   */

  unRegisterFields(context: string, pageReference: string, fields: string[] | undefined, subscriptionId: string) {
    if (!this.cascadeSubscribers[context]) {
      return;
    }

    fields?.forEach((field) => {
      if (!this.cascadeSubscribers[context][`${pageReference}${field}`]) {
        return;
      }

      const fullPathRef: CascadeSubscription = this.cascadeSubscribers[context][`${pageReference}${field}`];
      if (fullPathRef[subscriptionId]) {
        delete fullPathRef[subscriptionId];
      }
    });
  }

  /**
   * Invokes subscribers registered to the fields.
   * @description invokes subscribers registered to the fields.
   *
   * @example <caption>Example for triggerCascade() depicting how to triggerCascade for subscribers </caption>
   * Example usage - CascadeManager.triggerCascade('app/Primary', 'pgRef0', 'value 0')
   *
   * @param context name of the context
   * @param propertyReference reference path
   * @param propertyValue property value
   *
   * @private
   */

  triggerCascade(context: string, propertyReference: string, propertyValue: string, changeSet?: Set<Function>) {
    let isUpdatePrevValue = false;
    if (!this.cascadeSubscribers[context]) {
      return;
    }

    if (!this.cascadeSubscribers[context][propertyReference]) {
      return;
    }
    Object.keys(this.cascadeSubscribers[context][propertyReference]).forEach((id) => {
      const refObj: CascadeSubscription = this.cascadeSubscribers[context][propertyReference];
      if (id !== 'previousValue' && refObj.previousValue !== propertyValue) {
        isUpdatePrevValue = true;
        if (changeSet) {
          changeSet.add(refObj[id]);
        } else {
          refObj[id](propertyReference, propertyValue);
        }
      }
    });
    if (isUpdatePrevValue) {
      this.cascadeSubscribers[context][propertyReference].previousValue = propertyValue;
    }
  }

  /* @private */
  purgeCascadeFields(context: string) {
    delete this.cascadeSubscribers[context];
  }

  /**
   * @function handleServerChanges
   * @param context app context
   * @param {string[]} changedListProperty changed list properties
   * @param {string[]} leafProperty all leaf properties
   * It trigger cascade for all subscribed leaf property and generate pageinstruction for changed list property after server DT.
   * @private
   */
  handleServerChanges(context: string, changedListProperty: string[] = [], leafProperty: string[] = []) {
    if (!context) {
      return;
    }
    const changeSet: Set<Function> = new Set();

    leafProperty.forEach((propertyReference) => {
      const value = StateManager.getValue(propertyReference, '', context);
      this.triggerCascade(context, propertyReference, value, changeSet);
    });

    changeSet.forEach((callbackItem: Function) => {
      callbackItem();
    });

    if (this.listChangeSubscribers.length === 0 || changedListProperty.length === 0) {
      return;
    }

    changedListProperty.forEach((propertyReference) => {
      this.listChangeSubscribers.forEach((subscriber) => {
        const value = StateManager.getValue(propertyReference, '', context);
        subscriber(context, propertyReference, value);
      });
    });
  }

  /*
  @private
  */
  _updateChangedProperties(
    previousValue: unknown,
    serverFields: unknown,
    isChangedListPropertyExist: boolean,
    pathPrefix: string,
    context: string,
    changedListProperty: string[],
    changedPropertyWithError: string[],
    lengthMismatchPageListArr: string[]
  ) {
    const shouldInvolveInChangedProperties =
      isChangedListPropertyExist && lengthMismatchPageListArr.some((prefix) => pathPrefix.startsWith(prefix));
    if (previousValue !== serverFields || shouldInvolveInChangedProperties) {
      const lastIndexOfDot = pathPrefix.lastIndexOf('.');
      const pageReference = pathPrefix.substring(0, lastIndexOfDot);
      const property = pathPrefix.substring(lastIndexOfDot);
      const errorMessages = MessageManager.getMessages({
        type: 'error',
        property,
        context,
        pageReference
      });
      if (errorMessages?.length > 0) {
        changedPropertyWithError.push(pathPrefix);
      }
      if (!isChangedListPropertyExist) {
        return;
      }
      changedListProperty.push(pathPrefix);
    }
  }

  /*
  @private
  */
  isChangedListPropertyExist(pathPrefix: string) {
    let isChangedListPropertyExist = true;
    if (this.listChangeSubscribers.length === 0 || !Utils.isValidPageListProperty(pathPrefix)) {
      isChangedListPropertyExist = false;
    }
    return isChangedListPropertyExist;
  }

  /*
  @private
  */
  _getServerProperties(
    serverFields: any,
    context: string,
    pathPrefix: string,
    changedListProperty: string[],
    leafProperty: string[],
    changedPropertyWithError: string[],
    lengthMismatchPageListArr: string[]
  ) {
    if (typeof serverFields !== 'object') {
      leafProperty.push(pathPrefix.replace(/\.(\d+)/g, '[$1]'));
      const isChangedListPropertyExist = this.isChangedListPropertyExist(pathPrefix);
      pathPrefix = pathPrefix.replace(/\.(\d+)/g, '[$1]');
      const previousValue = StateManager.getValue(pathPrefix, '', context);
      const exceptionFalseValue = serverFields !== 0;
      /* istanbul ignore next */
      if (previousValue === undefined && !serverFields && exceptionFalseValue) {
        return;
      }

      this._updateChangedProperties(
        previousValue,
        serverFields,
        isChangedListPropertyExist,
        pathPrefix,
        context,
        changedListProperty,
        changedPropertyWithError,
        lengthMismatchPageListArr
      );
      return;
    }

    const ServerFieldKeys = Object.keys(serverFields ?? {});

    for (let i = 0, length = ServerFieldKeys.length; i < length; i += 1) {
      const key = ServerFieldKeys[i];
      const nestedPath = `${pathPrefix}.${key}`;
      if (Array.isArray(serverFields[key])) {
        const previousValue = StateManager.getValue(nestedPath, '', context);
        if (!previousValue || previousValue?.length !== serverFields[key].length) {
          lengthMismatchPageListArr.push(nestedPath.replace(/\.(\d+)/g, '[$1]'));
        }
      }
      this._getServerProperties(
        serverFields[key],
        context,
        nestedPath,
        changedListProperty,
        leafProperty,
        changedPropertyWithError,
        lengthMismatchPageListArr
      );
    }
  }

  /**
   * @function getServerProperties
   * @param {object} serverData serverData
   * @param {string }context app context
   * @param {string} path path from serverData
   * @returns {object}  {changedListProperty,leafProperty}
   * return leafProperty and changedListProperty
   * @private
   */
  getServerProperties(serverData: object, context: string, path: string = constants.CASE_INFO.CASE_INFO_CONTENT) {
    const changedListProperty: string[] = [];
    const changedPropertyWithError: string[] = [];
    const leafProperty: string[] = [];
    const lengthMismatchPageListArr: string[] = [];
    const serverFields = Utils.getIn(serverData, path);
    if (!serverFields || !context) {
      return { changedListProperty, leafProperty, changedPropertyWithError };
    }
    this._getServerProperties(
      serverFields,
      context,
      path,
      changedListProperty,
      leafProperty,
      changedPropertyWithError,
      lengthMismatchPageListArr
    );
    return { changedListProperty, leafProperty, changedPropertyWithError };
  }

  /**
  @private
  */
  _handlePageReference(pageReference: string, target: string, fieldType: string) {
    let pageRef = pageReference;

    /*
      target -> .EmbeddedPage.NestedPage.Property1
      pageReference -> caseInfo.content.EmbeddedPage.NestedPage
    */

    if (fieldType === 'Page' && target.startsWith('.') && target.slice(1).includes('.')) {
      const refpath = target.substring(0, target.lastIndexOf('.'));
      pageRef = `${pageReference}${refpath}`;
    }
    return pageRef;
  }

  /**
   * registers the provided function on every redux update which came from server.
   * @param callback a callback function which needs to be called when the redux changes
   * @private
   */
  registerForListChanges(callback: Function = () => {}) {
    this.listChangeSubscribers.push(callback);
  }

  /**
   * This will unRegister all the existing subscribers
   * @private
   */
  unRegisterForListChanges() {
    this.listChangeSubscribers = [];
  }
}

export default new CascadeManager();
